import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AvatarImage } from './avatar-image'
import { useDraggablePip } from '../hooks/use-draggable-pip'
import { useVideoElementReady } from '../hooks/use-video-element-ready'
import { useResolvedPresence } from '../stores/presence-store'
import { useVoiceCall } from '../contexts/voice-call-context'
import { useRemoteCallPeer } from '../hooks/use-remote-call-peer'
import { useVoiceCallSession } from '../hooks/use-voice-call-session'
import { useAuth } from '../contexts/auth-context'
import { TransferSheet } from '../features/calls/components/transfer-sheet'
import { TransferEligibilityDialog } from '../features/calls/components/transfer-eligibility-dialog'
import { AddPeoplePickerSheet } from '../features/calls/components/add-people-picker-sheet'
import { TransferInProgressBar } from '../features/calls/components/transfer-in-progress-bar'
import { useCallTransfer } from '../features/calls/hooks/use-call-transfer'
import { useInCallChat } from '../features/group-call/hooks/use-in-call-chat'
import { useInCallChatAlert } from '../features/group-call/hooks/use-in-call-chat-alert'
import { useCallConnectStatus } from '../features/calls/hooks/use-call-connect-status'
import { useEndedCallRecallReady } from '../hooks/use-ended-call-recall-ready'
import { useFullscreen } from '../features/calls/hooks/use-fullscreen'
import { CallReconnectingBanner } from '../features/calls/components/call-reconnecting-banner'
import { CallChatDrawer } from '../features/calls/components/call-chat-drawer'
import { resolveSelfUserId } from '../services/chat-service'
import { MinimizeOverlayButton } from './voice-call-overlay'
import { isDesktopWeb, isNativeApp } from '../utils/platform'
import { BRAND_LOGO_URL } from '../utils/brand-logo'
import { minimizedCallOverlayClassName } from '../utils/call-overlay-layers'
import { isCallPermissionSettingsMessage } from '../utils/request-call-permissions'
import { openNativeAppSettings } from '../services/voice-call/native-audio-route'
import { CallAudioRouteControl } from '../features/calls/components/call-audio-route-control'
import { SessionCallContextChip } from '../features/sessions/components/session-call-context-chip'
import { allowsExtraParties } from '../features/calls/utils/session-call-controls'

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.25 1.01l-2.2 2.22z" />
    </svg>
  )
}

function CameraIcon({ off }: { off: boolean }) {
  if (off) {
    return (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22M5 7h3l2-2h4l2 2h4a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" />
      </svg>
    )
  }
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function MicIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V5a3 3 0 00-5.94-.6" />
        <path d="M12 19v3M8 22h8M12 15a3 3 0 01-3-3V5a3 3 0 016 0v7" />
      </svg>
    )
  }
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v1a7 7 0 01-14 0v-1M12 19v3M8 22h8" />
    </svg>
  )
}

function PersonAddIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M22 11h-6" />
    </svg>
  )
}

function TransferIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
    </svg>
  )
}

function ScreenShareIcon({ active }: { active: boolean }) {
  if (active) {
    return (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22" />
        <path d="M3 5.5A1.5 1.5 0 014.5 4H19M21 7v8a2 2 0 01-2 2H9M3 8v7a2 2 0 002 2h2M8 21h8M12 17v4" />
      </svg>
    )
  }
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4M9 10.5l3-2.5 3 2.5M12 8v5" />
    </svg>
  )
}

function FullscreenIcon({ active }: { active: boolean }) {
  if (active) {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" />
      </svg>
    )
  }
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />
    </svg>
  )
}

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function VideoCallConnectingOverlay({
  message,
  peerName,
}: {
  message: string
  peerName?: string
}) {
  return (
    <div
      className="absolute inset-0 z-[5] flex flex-col items-center justify-center bg-gradient-to-b from-brand-900 to-slate-900 text-white"
      role="status"
      aria-live="polite"
      aria-label={peerName ? `${message} — ${peerName}` : message}
    >
      <img
        src={BRAND_LOGO_URL}
        alt=""
        className="video-call-connect-logo h-24 w-24 object-contain drop-shadow-lg sm:h-28 sm:w-28"
        width={112}
        height={112}
      />
      {peerName ? (
        <h2 className="mt-5 text-xl font-bold text-white">{peerName}</h2>
      ) : null}
      <p className={`max-w-xs px-6 text-center text-base font-medium text-white/90 ${peerName ? 'mt-2' : 'mt-6'}`}>
        {message}
      </p>
    </div>
  )
}

function PipLoadingSpinner() {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center rounded-xl bg-slate-800/90"
      aria-hidden
    >
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/25 border-t-white" />
    </div>
  )
}

export function VideoCallOverlay() {
  const session = useVoiceCallSession()
  const { user } = useAuth()
  const currentUserId = resolveSelfUserId(user?.userId)
  const remotePeer = useRemoteCallPeer()
  const peerPresence = useResolvedPresence(remotePeer)
  const connectStatus = useCallConnectStatus(session)
  const showRecall = useEndedCallRecallReady(session)
  const fullscreen = useFullscreen<HTMLDivElement>()
  const {
    sheetOpen,
    setSheetOpen,
    warnOpen,
    eligibility,
    pendingTarget,
    bookingCallback,
    onTargetSelected,
    onWarnChoice,
  } = useCallTransfer(currentUserId)
  const {
    requestAcceptCall,
    declineCall,
    hangUp,
    recallLastCall,
    downgradeToVoice,
    addPeople,
    startScreenShare,
    stopScreenShare,
    toggleMute,
    toggleCamera,
    resumeDrivingVideo,
    setRemoteAudioElement,
    setVideoElements,
    setContentVideoElement,
    refreshContentVideoBinding,
  } = useVoiceCall()
  const audioRef = useRef<HTMLAudioElement>(null)
  const localVideoRef = useRef<HTMLVideoElement | null>(null)
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null)
  const contentVideoRef = useRef<HTMLVideoElement | null>(null)
  const videoAreaRef = useRef<HTMLDivElement>(null)
  const canScreenShare = isDesktopWeb()
  const [now, setNow] = useState(Date.now())
  const [showAddPeople, setShowAddPeople] = useState(false)
  const [showChat, setShowChat] = useState(false)
  // In-call chat: delivered live to participants AND persisted into the
  // originating conversation thread (when the call has one) like a normal message.
  const chat = useInCallChat(session?.callId, currentUserId, session?.conversationId, {
    presenting: Boolean(
      session && session.mediaType === 'video' && (!session.isGroup || session.transferRoom),
    ),
    joined: session?.phase === 'active',
    isGroup: false,
    panelOpen: showChat,
  })
  const chatAlert = useInCallChatAlert(chat.messages, currentUserId, showChat)
  // Chat visibility is per-call: the overlay stays mounted between calls, so
  // without this a new call inherits the previous call's open chat drawer
  // (2026-10-07 field bug: the next call "automatically opened the chat").
  const chatResetCallId = session?.callId ?? null
  useEffect(() => {
    setShowChat(false)
  }, [chatResetCallId])
  const resolveChatName = useCallback(
    (senderId: string) => {
      if (senderId === currentUserId) return 'You'
      return remotePeer?.displayName ?? session?.peer.displayName ?? 'Participant'
    },
    [currentUserId, remotePeer, session?.peer.displayName],
  )
  const { pipStyle, pipHandlers } = useDraggablePip(videoAreaRef, {
    defaultInset: { bottom: 96, right: 16 },
    pipSize: { width: 112, height: 144 },
  })

  const syncVideoElements = useCallback(() => {
    setVideoElements(localVideoRef.current, remoteVideoRef.current)
  }, [setVideoElements])

  const bindLocalVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      localVideoRef.current = el
      setVideoElements(el, remoteVideoRef.current)
    },
    [setVideoElements],
  )

  const bindRemoteVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      remoteVideoRef.current = el
      setVideoElements(localVideoRef.current, el)
    },
    [setVideoElements],
  )

  const bindContentVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      contentVideoRef.current = el
      setContentVideoElement(el)
    },
    [setContentVideoElement],
  )

  // A transferRoom is a multi-party room under the hood but renders as a 1:1
  // call, so this overlay (not the group overlay) owns its audio element.
  const isActiveVideoCall = Boolean(
    session && session.mediaType === 'video' && (!session.isGroup || session.transferRoom),
  )

  const bindAudioRef = useCallback(
    (el: HTMLAudioElement | null) => {
      audioRef.current = el
      if (isActiveVideoCall) {
        setRemoteAudioElement(el)
      }
    },
    [isActiveVideoCall, setRemoteAudioElement],
  )

  useLayoutEffect(() => {
    if (!isActiveVideoCall) return
    setRemoteAudioElement(audioRef.current)
    return () => setRemoteAudioElement(null)
  }, [isActiveVideoCall, setRemoteAudioElement, session?.callId])

  // Sync refs after DOM commit; only clear when the call ends (callId change / unmount).
  // Do not depend on phase — clearing refs on phase transitions races Chime join on web.
  useLayoutEffect(() => {
    syncVideoElements()
    return () => setVideoElements(null, null)
  }, [setVideoElements, session?.callId, syncVideoElements])

  useLayoutEffect(() => {
    if (!session?.remotePresenting) return
    refreshContentVideoBinding()
  }, [refreshContentVideoBinding, session?.callId, session?.remotePresenting])

  useEffect(() => {
    if (session?.phase !== 'active' || !session.connectedAt) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [session?.phase, session?.connectedAt])

  const showVideo =
    session?.phase === 'active' || session?.phase === 'connecting'
  const remoteVideoReady = useVideoElementReady(remoteVideoRef, showVideo, session?.callId)
  const localVideoReady = useVideoElementReady(localVideoRef, showVideo, session?.callId)

  if (!session || session.mediaType !== 'video' || (session.isGroup && !session.transferRoom))
    return null

  if (session.phase === 'elsewhere') return null

  // iOS: CallKit is presenting this incoming call — it is the one canonical
  // ring surface, so the in-app ring overlay stands down. The overlay appears
  // as soon as the user answers (osRingActive clears / phase leaves incoming).
  if (session.phase === 'incoming' && session.osRingActive) return null

  const displayPeer = remotePeer ?? session.peer
  const remoteDisplayName = displayPeer.displayName

  const duration =
    session.phase === 'active' && session.connectedAt
      ? formatDuration(now - session.connectedAt)
      : null

  const status = connectStatus.label
  const showControls =
    session.phase === 'incoming' ||
    session.phase === 'active' ||
    session.phase === 'connecting' ||
    session.phase === 'outgoing'
  const showRemoteConnectingOverlay =
    session.phase === 'outgoing' ||
    session.phase === 'incoming' ||
    session.phase === 'connecting' ||
    (session.phase === 'active' &&
      !remoteVideoReady &&
      // Video is deliberately paused for driving — not "connecting".
      session.peerDriving !== true &&
      session.selfDriving !== true)
  const connectingMessage =
    session.phase === 'connecting' || session.phase === 'active'
      ? 'Connecting your video now'
      : connectStatus.label
  const showPeerNameOnOverlay =
    session.phase === 'outgoing' ||
    session.phase === 'incoming' ||
    session.phase === 'connecting'

  const isActive = session.phase === 'active'
  const isPresenting = Boolean(session.isPresenting)
  const remotePresenting = Boolean(session.remotePresenting)
  // In-car video pause (CarPlay / Android Auto). Self: camera never runs and
  // the peer's video stays paused behind a full-area panel. Peer: their tile
  // is replaced with the "<Name> is driving" placeholder.
  const selfDriving = Boolean(session.selfDriving)
  const peerDriving = Boolean(session.peerDriving)
  // The self-view pip is pointless while driving (camera is off by policy) and
  // would float above the driving panel.
  const showPip = showVideo && !selfDriving
  const showScreenShareButton = canScreenShare && (isActive || session.phase === 'connecting')
  // Minimized: keep mounted (audio + video tiles stay bound/playing) but hidden;
  // the ReturnToCallBanner re-presents it. Never hide an incoming ring. Keyed on
  // the EXPLICIT user-minimize flag only (not background) so a missed foreground
  // event can't strand the call UI hidden. Hiding also disables hit-testing
  // (pointer-events-none) — `invisible` alone left Android's composited <video>
  // surfaces swallowing taps meant for the return-to-call banner.
  const minimized = Boolean(session.userMinimized) && session.phase !== 'incoming'
  const canMinimize =
    isActive || session.phase === 'connecting' || session.phase === 'outgoing'

  const toggleScreenShare = () => {
    if (isPresenting) void stopScreenShare()
    else void startScreenShare()
  }

  return (
    <div
      ref={fullscreen.ref}
      className={`fixed inset-0 z-[60] flex flex-col bg-slate-950 native-modal-top native-modal-bottom ${minimizedCallOverlayClassName(
        minimized,
      )}`}
      data-dark-surface={minimized ? undefined : 'true'}
      role="dialog"
      aria-modal={!minimized}
      aria-hidden={minimized}
      aria-label="Video call"
    >
      <audio ref={bindAudioRef} data-call-remote="true" className="sr-only" aria-hidden playsInline />

      {canMinimize && !minimized && <MinimizeOverlayButton />}

      <CallReconnectingBanner show={connectStatus.isReconnecting} />

      {session.notice && (
        <div
          className="pointer-events-none absolute inset-x-0 top-[max(1rem,var(--safe-top))] z-[62] flex justify-center px-4"
          role="status"
          aria-live="polite"
        >
          <span className="rounded-full bg-slate-900/85 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {session.notice}
          </span>
        </div>
      )}

      {chatAlert.banner && !showChat && (
        <button
          type="button"
          onClick={() => setShowChat(true)}
          className="absolute inset-x-0 top-[max(3.75rem,calc(var(--safe-top)+3rem))] z-[63] mx-auto flex max-w-sm items-start gap-2 rounded-2xl bg-slate-900/90 px-4 py-3 text-left text-white shadow-xl transition hover:bg-slate-900"
          role="status"
          aria-live="polite"
        >
          <span className="mt-0.5 shrink-0 text-emerald-300">
            <ChatIcon />
          </span>
          <span className="min-w-0">
            <span className="block text-xs font-semibold text-emerald-200">
              {resolveChatName(chatAlert.banner.senderId)} · in-call message
            </span>
            <span className="block truncate text-sm text-white/90">{chatAlert.banner.text}</span>
          </span>
        </button>
      )}

      <div ref={videoAreaRef} className="relative min-h-0 flex-1 bg-slate-950">
        {/* Screen-share (content) tile — the main view while someone presents.
            Kept mounted (off-screen) otherwise so late tile binding has a target. */}
        <video
          ref={bindContentVideoRef}
          className={`absolute inset-0 h-full w-full bg-black object-contain object-center transition-opacity duration-300 ${
            remotePresenting ? 'z-[1] opacity-100' : 'pointer-events-none opacity-0'
          }`}
          autoPlay
          playsInline
          muted
          aria-label={remotePresenting ? `${remoteDisplayName} is presenting` : undefined}
        />
        <video
          ref={bindRemoteVideoRef}
          className={
            remotePresenting
              ? 'absolute left-[max(0.75rem,var(--safe-left))] top-16 z-[2] h-24 w-36 rounded-xl border-2 border-white/30 bg-slate-800 object-cover shadow-lg sm:h-28 sm:w-44'
              : `pointer-events-none absolute inset-0 h-full w-full bg-slate-950 object-contain object-center transition-opacity duration-300 ${
                  showVideo && remoteVideoReady ? 'opacity-100' : 'opacity-0'
                }`
          }
          autoPlay
          playsInline
          muted={false}
        />
        {(isPresenting || remotePresenting) && (
          <div className="absolute inset-x-0 top-12 z-20 flex justify-center px-4" aria-live="polite">
            <span className="rounded-full bg-black/70 px-4 py-1.5 text-sm font-semibold text-white shadow-lg">
              {isPresenting ? 'You are sharing your screen' : `${remoteDisplayName} is presenting`}
            </span>
          </div>
        )}
        {showRemoteConnectingOverlay && (
          <VideoCallConnectingOverlay
            message={connectingMessage}
            peerName={showPeerNameOnOverlay ? remoteDisplayName : undefined}
          />
        )}
        {/* Self-healing: the placeholder also requires that remote video is NOT
            actually flowing. A genuinely driving peer sends no video, so the
            extra condition is always true while real — but if their
            driving:false announce is lost (single best-effort publish), the
            moment their video frames arrive the placeholder yields instead of
            covering live video for the rest of the call. */}
        {isActive && peerDriving && !selfDriving && !remoteVideoReady && (
          <div
            className="absolute inset-0 z-[6] flex flex-col items-center justify-center bg-gradient-to-b from-brand-900 to-slate-900 px-6 text-center text-white"
            role="status"
            aria-live="polite"
          >
            <AvatarImage
              name={remoteDisplayName}
              avatarUrl={displayPeer.avatarUrl}
              size={96}
              variant="provider"
              className="ring-4 ring-white/20"
              fallback="logo"
            />
            <h2 className="mt-4 text-xl font-bold">{remoteDisplayName}</h2>
            <p className="mt-2 max-w-xs text-base font-medium text-white/90">
              {remoteDisplayName} is driving — video paused
            </p>
            <p className="mt-1 text-sm text-white/70">You can keep talking — audio stays on.</p>
          </div>
        )}
        {/* Only once ACTIVE — while connecting, the connecting overlay (z-[5])
            must stay visible so a car-mode caller isn't told "the call stays
            connected" before media has connected. */}
        {isActive && selfDriving && (
          <div
            className="absolute inset-0 z-[6] flex flex-col items-center justify-center bg-gradient-to-b from-brand-900 to-slate-900 px-6 text-center text-white"
            role="status"
            aria-live="polite"
          >
            <AvatarImage
              name={remoteDisplayName}
              avatarUrl={displayPeer.avatarUrl}
              size={96}
              variant="provider"
              className="ring-4 ring-white/20"
              presence={peerPresence}
              fallback="logo"
            />
            <h2 className="mt-4 text-xl font-bold">{remoteDisplayName}</h2>
            <p className="mt-2 max-w-xs text-base font-medium text-white/90">
              Video paused while you're driving
            </p>
            <p className="mt-1 max-w-xs text-sm text-white/70">
              The call stays connected. Keep your eyes on the road.
            </p>
            {isActive && (
              <button
                type="button"
                onClick={() => resumeDrivingVideo()}
                className="mt-6 min-h-12 rounded-full bg-white/15 px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                I've stopped — turn video on
              </button>
            )}
          </div>
        )}
        {!showVideo && !showRemoteConnectingOverlay && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-brand-900 to-slate-900 text-white">
            <AvatarImage
              name={remoteDisplayName}
              avatarUrl={displayPeer.avatarUrl}
              size={96}
              variant="provider"
              className="ring-4 ring-white/20"
              presence={peerPresence}
              fallback="logo"
            />
            <h2 className="mt-4 text-xl font-bold">{remoteDisplayName}</h2>
            <p className="mt-1 text-sm text-white/80" aria-live="polite">
              {status}
            </p>
            <SessionCallContextChip sessionUuid={session.sessionUuid} className="mt-3" />
          </div>
        )}
        <div
          {...(showPip ? pipHandlers : {})}
          style={showPip ? pipStyle : undefined}
          className={
            showPip
              ? 'absolute z-10 touch-none'
              : 'pointer-events-none absolute bottom-24 right-4 z-10 opacity-0'
          }
        >
          <video
            ref={bindLocalVideoRef}
            className={`h-28 w-20 rounded-xl border-2 border-white/30 bg-slate-800 object-cover shadow-lg sm:h-36 sm:w-28 ${
              session.isCameraOff ? 'opacity-0' : ''
            }`}
            autoPlay
            playsInline
            muted
            aria-hidden={!showPip}
          />
          {session.isCameraOff && showPip && (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-slate-800/90 text-white">
              <CameraIcon off />
            </div>
          )}
          {showPip && !localVideoReady && !session.isCameraOff && <PipLoadingSpinner />}
        </div>
        {(session.phase === 'active' || session.phase === 'connecting') && duration && (
          <div
            className="absolute inset-x-0 top-0 z-20 flex items-center justify-center bg-gradient-to-b from-black/70 to-transparent px-4 pb-6 pt-[max(0.75rem,var(--safe-top))]"
            aria-live="polite"
          >
            <span className="rounded-full bg-black/60 px-4 py-1.5 text-base font-semibold tabular-nums tracking-wide text-white">
              {duration}
            </span>
            <SessionCallContextChip sessionUuid={session.sessionUuid} className="ml-2 bg-black/60" />
          </div>
        )}
        {fullscreen.supported && (isActive || session.phase === 'connecting') && (
          <button
            type="button"
            onClick={fullscreen.toggle}
            className="absolute right-[max(0.75rem,var(--safe-right))] top-[max(0.75rem,var(--safe-top))] z-20 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white shadow-lg transition hover:bg-black/70"
            aria-label={fullscreen.isFullscreen ? 'Exit full screen' : 'Enter full screen'}
            aria-pressed={fullscreen.isFullscreen}
            title={fullscreen.isFullscreen ? 'Exit full screen' : 'Full screen'}
          >
            <FullscreenIcon active={fullscreen.isFullscreen} />
          </button>
        )}
      </div>

      {session.errorMessage && session.phase !== 'ended' && (
        <div className="px-4 py-2 text-center" role="status">
          <p className="text-xs text-amber-300">{session.errorMessage}</p>
          {isNativeApp() && isCallPermissionSettingsMessage(session.errorMessage) && (
            <button
              type="button"
              onClick={() => void openNativeAppSettings()}
              className="mt-2 rounded-lg bg-white/15 px-4 py-2 text-xs font-semibold text-white transition hover:bg-white/25"
            >
              Open app settings
            </button>
          )}
        </div>
      )}

      {showControls && (
        <div className="shrink-0 border-t border-white/10 bg-slate-950/90 px-6 py-5">
          <TransferInProgressBar transfer={session.transfer} />
          <p className="mb-4 text-center text-sm text-white/80" aria-live="polite">
            {status}
          </p>
          <div className="flex items-center justify-center gap-4">
            {session.phase === 'incoming' ? (
              <>
                <button
                  type="button"
                  onClick={() => void declineCall()}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600"
                  aria-label="Decline video call"
                >
                  <PhoneIcon className="h-7 w-7 rotate-[135deg]" />
                </button>
                <button
                  type="button"
                  onClick={() => requestAcceptCall()}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg transition hover:bg-emerald-600"
                  aria-label="Accept video call"
                >
                  <PhoneIcon className="h-7 w-7" />
                </button>
              </>
            ) : (
              <>
                {(session.phase === 'active' || session.phase === 'connecting') && (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleMute()}
                      className={`flex h-14 w-14 items-center justify-center rounded-full text-white transition ${
                        session.isMuted ? 'bg-white/30 ring-2 ring-white/50' : 'bg-white/10 hover:bg-white/20'
                      }`}
                      aria-label={session.isMuted ? 'Unmute microphone' : 'Mute microphone'}
                      aria-pressed={session.isMuted}
                    >
                      <MicIcon muted={session.isMuted} />
                    </button>
                    <CallAudioRouteControl active compact />
                    {/* While driving, the dedicated "I've stopped" control is the
                        only way to turn video on — hide the camera toggle. */}
                    {!selfDriving && (
                      <button
                        type="button"
                        onClick={() => void toggleCamera()}
                        className={`flex h-14 w-14 items-center justify-center rounded-full text-white transition ${
                          session.isCameraOff ? 'bg-white/30 ring-2 ring-white/50' : 'bg-white/10 hover:bg-white/20'
                        }`}
                        aria-label={session.isCameraOff ? 'Turn camera on' : 'Turn camera off'}
                        aria-pressed={Boolean(session.isCameraOff)}
                      >
                        <CameraIcon off={Boolean(session.isCameraOff)} />
                      </button>
                    )}
                    {session.phase === 'active' && (
                      <button
                        type="button"
                        onClick={() => void downgradeToVoice()}
                        disabled={session.downgradingToVoice}
                        title="Switch to voice"
                        className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 disabled:opacity-50"
                        aria-label="Switch to voice call"
                      >
                        <PhoneIcon className="h-7 w-7" />
                      </button>
                    )}
                    {showScreenShareButton && (
                      <button
                        type="button"
                        onClick={toggleScreenShare}
                        title={isPresenting ? 'Stop sharing your screen' : 'Share your screen'}
                        className={`flex h-14 w-14 items-center justify-center rounded-full transition ${
                          isPresenting
                            ? 'bg-emerald-400 text-emerald-950 ring-2 ring-emerald-200/80 hover:bg-emerald-300'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                        aria-label={isPresenting ? 'Stop sharing your screen' : 'Share your screen'}
                        aria-pressed={isPresenting}
                      >
                        <ScreenShareIcon active={isPresenting} />
                      </button>
                    )}
                    {/* A telehealth session call is exactly provider + member:
                        no transfer, no extra participants. */}
                    {session.phase === 'active' && allowsExtraParties(session) && (
                      <button
                        type="button"
                        onClick={() => setSheetOpen(true)}
                        title="Transfer call"
                        className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                        aria-label="Transfer call"
                      >
                        <TransferIcon />
                      </button>
                    )}
                    {session.phase === 'active' && allowsExtraParties(session) && (
                      <button
                        type="button"
                        onClick={() => setShowAddPeople(true)}
                        title="Add person to call"
                        className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                        aria-label="Add person to call"
                      >
                        <PersonAddIcon />
                      </button>
                    )}
                    {session.phase === 'active' && (
                      <button
                        type="button"
                        onClick={() => setShowChat((v) => !v)}
                        title="In-call chat"
                        className={`relative flex h-14 w-14 items-center justify-center rounded-full transition ${
                          showChat
                            ? 'bg-emerald-400 text-emerald-950 ring-2 ring-emerald-200/80 hover:bg-emerald-300'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                        aria-label={chatAlert.hasUnread ? 'In-call chat — new message' : 'In-call chat'}
                        aria-pressed={showChat}
                      >
                        <ChatIcon />
                        {chatAlert.hasUnread && !showChat && (
                          <span
                            className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-red-500 ring-2 ring-slate-950"
                            aria-hidden
                          />
                        )}
                      </button>
                    )}
                  </>
                )}
                <button
                  type="button"
                  onClick={() => void hangUp()}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600"
                  aria-label="End video call"
                >
                  <PhoneIcon className="h-7 w-7 rotate-[135deg]" />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showRecall && (
        <div className="shrink-0 px-6 pb-6">
          <button
            type="button"
            onClick={() => void recallLastCall()}
            className="w-full rounded-xl bg-white/15 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/25"
          >
            Try again
          </button>
        </div>
      )}

      <TransferSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onConfirm={onTargetSelected}
        excludeUserIds={
          session
            ? [session.callerId === currentUserId ? session.calleeId : session.callerId]
            : []
        }
      />
      <TransferEligibilityDialog
        open={warnOpen}
        targetName={pendingTarget?.displayName ?? 'this user'}
        result={eligibility}
        bookingCallback={bookingCallback}
        onChoose={(choice) => void onWarnChoice(choice)}
      />
      <AddPeoplePickerSheet
        open={showAddPeople}
        onClose={() => setShowAddPeople(false)}
        onAdd={(users) => void addPeople(users)}
        excludeUserIds={
          session
            ? [session.callerId, session.calleeId, currentUserId ?? ''].filter(Boolean)
            : []
        }
      />
      <CallChatDrawer
        open={showChat && session.phase === 'active'}
        onClose={() => setShowChat(false)}
        chat={chat}
        resolveName={resolveChatName}
        placeholder={`Message ${remoteDisplayName}…`}
      />
    </div>
  )
}
